import { useState, useEffect } from 'react';
import { getOwnerDisplayName } from '../AccountCard';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Home,
  Car,
  Briefcase,
  Gem,
  Banknote,
  Package,
  PiggyBank,
  TrendingUp,
  CreditCard,
  Edit2,
} from 'lucide-react';
import api from '../../lib/api';
import { money } from '../../lib/money';
import type { Asset, AssetCreate, Case, CaseLiability } from '../../types';
import {
  useLiabilities,
  useCreateLiability,
  useUpdateLiability,
  useDeleteLiability,
} from '../../hooks/useInventoryAppraisement';
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  EmptyState,
  IconButton,
  Input,
  Select,
  Skeleton,
  TextArea,
} from '../ui';
import IncomeCard from '../IncomeCard';
import { CHARACTERIZATION_OPTIONS, propertyStatusBadge, propertyStatusLabel } from '../../lib/enums';

// Values must match net_value_split.ASSET_TYPE_TO_NET keys so the net-value
// math + optimizer directives apply correctly. Retirement is split into
// Traditional (embedded income tax) vs Roth (already taxed). Credit cards are
// NOT here — they are debts and live in the Liabilities list.
const ASSET_TYPES = [
  { value: 'Bank Account', label: 'Bank / Cash (checking, savings)' },
  { value: 'Brokerage', label: 'Brokerage / Investment (taxable)' },
  { value: 'Retirement', label: 'Retirement — Traditional (401k / IRA)' },
  { value: 'Roth Retirement', label: 'Retirement — Roth (Roth IRA / 401k)' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Vehicle', label: 'Vehicle' },
  { value: 'Business', label: 'Business Interest' },
  { value: 'Personal Property', label: 'Personal Property' },
  { value: 'Jewelry', label: 'Jewelry' },
  { value: 'Other', label: 'Other' },
];

const PROPERTY_STATUSES = CHARACTERIZATION_OPTIONS;

const DEBT_TYPES = [
  { value: 'Mortgage', label: 'Mortgage' },
  { value: 'HELOC', label: 'HELOC (Home Equity Line)' },
  { value: 'Auto Loan', label: 'Auto Loan' },
  { value: 'Credit Card', label: 'Credit Card' },
  { value: 'Student Loan', label: 'Student Loan' },
  { value: 'Personal Loan', label: 'Personal Loan' },
  { value: 'Business Loan', label: 'Business Loan / Line of Credit' },
  { value: 'Promissory Note', label: 'Promissory Note' },
  { value: 'Tax Debt', label: 'Tax Debt (IRS / State)' },
  { value: 'Medical Debt', label: 'Medical Debt' },
  { value: '401k Loan', label: '401(k) / Retirement Loan' },
  { value: 'Attorney Fees', label: 'Attorney Fees' },
  { value: 'Other', label: 'Other' },
];

function getAssignedOptions(clientName?: string | null, opposingPartyName?: string | null) {
  // Canonical owner values (server/party.py) — the stored token says whose side
  // it is, not anyone's marital role.
  return [
    { value: '', label: 'Unassigned' },
    { value: 'Client', label: clientName ? `${clientName} (client)` : 'Client' },
    { value: 'Opposing', label: opposingPartyName ? `${opposingPartyName} (opposing party)` : 'Opposing party' },
  ];
}

const SELL_HORIZONS = [
  { value: 'SELL_NOW', label: 'Sell Now' },
  { value: 'HOLD_1_YEAR', label: 'Hold 1 Year' },
  { value: 'HOLD_3_YEARS', label: 'Hold 3 Years' },
  { value: 'HOLD_5_YEARS', label: 'Hold 5 Years' },
  { value: 'KEEP', label: 'Keep (No Sale)' },
];

const VALUE_SOURCE_LABELS: Record<string, string> = {
  zestimate: 'Zillow Zestimate',
  redfin: 'Redfin Estimate',
  appraisal: 'Appraisal',
  tax_appraisal: 'Tax Appraisal',
  estimate: 'Estimate',
  kbb: 'Kelley Blue Book',
  edmunds: 'Edmunds',
  nada: 'NADA',
  statement: 'Statement',
  manual: 'Manual',
};

function valueSourceLabel(source: string): string {
  return VALUE_SOURCE_LABELS[source] ?? source;
}

function getAssetIcon(type: string) {
  switch (type) {
    case 'Real Estate': return Home;
    case 'Vehicle': return Car;
    case 'Business': return Briefcase;
    case 'Retirement': return PiggyBank;
    case 'Brokerage': return TrendingUp;
    case 'Bank Account': return Banknote;
    case 'Jewelry': return Gem;
    default: return Package;
  }
}

const getPropertyBadgeVariant = propertyStatusBadge;
const getPropertyLabel = propertyStatusLabel;

function formatCurrency(val: number | null | undefined): string {
  return money(val);
}

interface AssetsTabProps {
  caseId: string;
  caseData: Case;
}

const emptyForm: AssetCreate = {
  asset_type: 'Real Estate',
  name: '',
  estimated_value: undefined,
  acquisition_date: undefined,
  property_status: 'CP_candidate',
  assigned_to: undefined,
  notes: undefined,
  description: undefined,
  address: undefined,
  debt_balance: undefined,
  cost_basis: undefined,
  sell_horizon: 'SELL_NOW',
};

const emptyDebtForm = {
  creditor_name: '',
  liability_type: 'Mortgage',
  balance: undefined as number | undefined,
  account_last_four: '',
  property_status: 'CP_candidate',
  assigned_to: '',
  notes: '',
};

export default function AssetsTab({ caseId, caseData }: AssetsTabProps) {
  const queryClient = useQueryClient();

  // ── Assets state ───────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<AssetCreate>({ ...emptyForm });
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<AssetCreate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Asset | null>(null);

  // ── Debts state ────────────────────────────────────────────────────────
  const [showDebtForm, setShowDebtForm] = useState(false);
  const [debtForm, setDebtForm] = useState({ ...emptyDebtForm });
  const [editDebtId, setEditDebtId] = useState<number | null>(null);
  const [editDebtForm, setEditDebtForm] = useState<typeof emptyDebtForm | null>(null);
  const [deleteDebtTarget, setDeleteDebtTarget] = useState<CaseLiability | null>(null);

  // ── Queries ────────────────────────────────────────────────────────────
  const { data: assets = [], isLoading } = useQuery<Asset[]>({
    queryKey: ['assets', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/assets`);
      return data;
    },
  });

  // §121 default: only the first real-estate asset is presumed the primary
  // residence; additional properties default to rental. Mirrors the backend.
  const firstRealEstateId = assets.find((a) => a.asset_type === 'Real Estate')?.id;

  const { data: liabilities = [] } = useLiabilities(caseId);

  // ── Asset mutations ────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: async (data: AssetCreate) => {
      const { data: result } = await api.post(`/cases/${caseId}/assets`, data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', caseId] });
      setForm({ ...emptyForm });
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (assetId: number) => {
      await api.delete(`/assets/${assetId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', caseId] });
      setDeleteTarget(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ assetId, data }: { assetId: number; data: AssetCreate }) => {
      const { data: result } = await api.patch(`/cases/${caseId}/assets/${assetId}`, data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', caseId] });
      setExpandedId(null);
      setEditForm(null);
    },
  });

  // ── Debt mutations ─────────────────────────────────────────────────────
  const createDebtMutation = useCreateLiability(caseId);
  const updateDebtMutation = useUpdateLiability(caseId);
  const deleteDebtMutation = useDeleteLiability(caseId);

  // ── Auto-classify assets by acquisition date ───────────────────────────
  useEffect(() => {
    if (form.acquisition_date && caseData.marriage_date) {
      const acqDate = new Date(form.acquisition_date);
      const marriageDate = new Date(caseData.marriage_date);
      if (acqDate < marriageDate && form.property_status === 'CP_candidate') {
        setForm(prev => ({ ...prev, property_status: 'SP_candidate' }));
      }
    }
  }, [form.acquisition_date, caseData.marriage_date, form.property_status]);

  useEffect(() => {
    if (editForm?.acquisition_date && caseData.marriage_date) {
      const acqDate = new Date(editForm.acquisition_date);
      const marriageDate = new Date(caseData.marriage_date);
      if (acqDate < marriageDate && editForm.property_status === 'CP_candidate') {
        setEditForm(prev => prev ? { ...prev, property_status: 'SP_candidate' } : prev);
      }
    }
  }, [editForm?.acquisition_date, caseData.marriage_date, editForm?.property_status]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const payload: AssetCreate = { ...form };
    if (!payload.estimated_value) delete payload.estimated_value;
    if (!payload.acquisition_date) delete payload.acquisition_date;
    if (!payload.assigned_to) delete payload.assigned_to;
    if (!payload.notes) delete payload.notes;
    if (!payload.description) delete payload.description;
    if (!payload.address) delete payload.address;
    if (!payload.debt_balance) delete payload.debt_balance;
    if (!payload.cost_basis) delete payload.cost_basis;
    createMutation.mutate(payload);
  };

  const handleExpand = (asset: Asset) => {
    if (expandedId === asset.id) {
      setExpandedId(null);
      setEditForm(null);
    } else {
      setExpandedId(asset.id);
      setEditForm({
        asset_type: asset.asset_type,
        name: asset.name,
        estimated_value: asset.estimated_value ?? undefined,
        acquisition_date: asset.acquisition_date ?? undefined,
        property_status: asset.property_status,
        assigned_to: asset.assigned_to ?? undefined,
        notes: asset.notes ?? undefined,
        description: asset.description ?? undefined,
        address: asset.address ?? undefined,
        debt_balance: asset.debt_balance ?? undefined,
        cost_basis: asset.cost_basis ?? undefined,
        sell_horizon: asset.sell_horizon ?? 'SELL_NOW',
        is_primary_residence: asset.is_primary_residence ?? undefined,
      });
    }
  };

  const handleSaveEdit = (assetId: number) => {
    if (!editForm || !editForm.name.trim()) return;
    updateMutation.mutate({ assetId, data: editForm });
  };

  const handleDebtSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!debtForm.creditor_name.trim()) return;
    createDebtMutation.mutate(
      {
        creditor_name: debtForm.creditor_name,
        liability_type: debtForm.liability_type || null,
        balance: debtForm.balance ?? null,
        account_last_four: debtForm.account_last_four || null,
        property_status: debtForm.property_status || 'CP_candidate',
        assigned_to: debtForm.assigned_to || null,
        notes: debtForm.notes || null,
      },
      {
        onSuccess: () => {
          setDebtForm({ ...emptyDebtForm });
          setShowDebtForm(false);
        },
      }
    );
  };

  const handleEditDebt = (l: CaseLiability) => {
    if (editDebtId === l.id) {
      setEditDebtId(null);
      setEditDebtForm(null);
    } else {
      setEditDebtId(l.id);
      setEditDebtForm({
        creditor_name: l.creditor_name,
        liability_type: l.liability_type || 'Mortgage',
        balance: l.balance ?? undefined,
        account_last_four: l.account_last_four || '',
        property_status: l.property_status || 'CP_candidate',
        assigned_to: l.assigned_to || '',
        notes: l.notes || '',
      });
    }
  };

  const handleSaveDebt = (id: number) => {
    if (!editDebtForm || !editDebtForm.creditor_name.trim()) return;
    updateDebtMutation.mutate(
      {
        id,
        updates: {
          creditor_name: editDebtForm.creditor_name,
          liability_type: editDebtForm.liability_type || null,
          balance: editDebtForm.balance ?? null,
          account_last_four: editDebtForm.account_last_four || null,
          property_status: editDebtForm.property_status || 'CP_candidate',
          assigned_to: editDebtForm.assigned_to || null,
          notes: editDebtForm.notes || null,
        },
      },
      {
        onSuccess: () => {
          setEditDebtId(null);
          setEditDebtForm(null);
        },
      }
    );
  };

  // ── Derived totals ─────────────────────────────────────────────────────
  const totalValue = assets.reduce((sum, a) => sum + (a.estimated_value || 0), 0);
  const totalDebt = liabilities.reduce((sum, l) => sum + (l.balance || 0), 0) +
    assets.reduce((sum, a) => sum + (a.debt_balance || 0), 0);

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading assets" className="space-y-4">
        <Skeleton variant="rect" height={28} width="33%" />
        <Skeleton variant="rect" height={88} className="rounded-lg" />
        <Skeleton variant="rect" height={88} className="rounded-lg" />
        <Skeleton variant="rect" height={88} className="rounded-lg" />
        <span className="sr-only">Loading assets</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">

      {/* ── INCOME SECTION ──────────────────────────────────────────────── */}
      <IncomeCard caseId={caseId} caseData={caseData} />

      {/* ── ASSETS SECTION ──────────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="t-section text-green-900">Assets & Property</h2>
            <p className="text-sm text-ink-faint mt-1">
              {assets.length} asset{assets.length !== 1 ? 's' : ''} · Total value: {formatCurrency(totalValue)}
            </p>
          </div>
          <Button size="lg" icon={Plus} onClick={() => setShowForm(!showForm)}>
            Add Asset
          </Button>
        </div>

        {showForm && (
          <Card>
            <form onSubmit={handleSubmit} className="space-y-4">
              <h3 className="t-card text-green-900">New Asset</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Asset Type"
                  options={ASSET_TYPES}
                  value={form.asset_type}
                  onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
                />
                <Input
                  label="Name"
                  placeholder="e.g., Family Home, 2020 Toyota Camry"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
                <Input
                  label="Estimated Value ($)"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.estimated_value ?? ''}
                  onChange={(e) => setForm({ ...form, estimated_value: e.target.value ? parseFloat(e.target.value) : undefined })}
                />
                <Input
                  label="Acquisition Date"
                  type="date"
                  value={form.acquisition_date ?? ''}
                  onChange={(e) => setForm({ ...form, acquisition_date: e.target.value || undefined })}
                />
                <Select
                  label="Property Status"
                  options={PROPERTY_STATUSES}
                  value={form.property_status || 'CP_candidate'}
                  onChange={(e) => setForm({ ...form, property_status: e.target.value })}
                />
                <Select
                  label="Assigned To"
                  options={getAssignedOptions(caseData.client_name, caseData.opposing_party_name)}
                  value={form.assigned_to || ''}
                  onChange={(e) => setForm({ ...form, assigned_to: e.target.value || undefined })}
                />
                <Input
                  label="Debt Balance ($)"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.debt_balance ?? ''}
                  onChange={(e) => setForm({ ...form, debt_balance: e.target.value ? parseFloat(e.target.value) : undefined })}
                />
                <Input
                  label="Cost Basis ($)"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.cost_basis ?? ''}
                  onChange={(e) => setForm({ ...form, cost_basis: e.target.value ? parseFloat(e.target.value) : undefined })}
                />
                <Select
                  label="Sell Horizon"
                  options={SELL_HORIZONS}
                  value={form.sell_horizon || 'SELL_NOW'}
                  onChange={(e) => setForm({ ...form, sell_horizon: e.target.value })}
                />
                <Input
                  label="Address"
                  placeholder="Property address (if applicable)"
                  value={form.address ?? ''}
                  onChange={(e) => setForm({ ...form, address: e.target.value || undefined })}
                />
              </div>
              <TextArea
                label="Notes"
                placeholder="Additional details about this asset..."
                value={form.notes ?? ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value || undefined })}
              />
              <div className="flex gap-2 justify-end">
                <Button variant="secondary" type="button" onClick={() => { setShowForm(false); setForm({ ...emptyForm }); }}>
                  Cancel
                </Button>
                <Button type="submit" loading={createMutation.isPending} disabled={!form.name.trim()}>
                  Save Asset
                </Button>
              </div>
            </form>
          </Card>
        )}

        {assets.length === 0 && !showForm ? (
          <Card padding="none">
            <EmptyState
              icon={Package}
              title="No assets added yet"
              description={'Click "Add Asset" to begin tracking property for this case.'}
            />
          </Card>
        ) : (
          <div className="space-y-3">
            {assets.map((asset) => {
              const Icon = getAssetIcon(asset.asset_type);
              const isExpanded = expandedId === asset.id;

              return (
                <Card key={asset.id} padding={false}>
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-cream transition-colors"
                    onClick={() => handleExpand(asset)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Icon size={20} className="text-green-700" />
                      </div>
                      <div>
                        <h4 className="font-medium text-green-900">{asset.name}</h4>
                        <p className="text-sm text-ink-faint">
                          {asset.asset_type}
                          {asset.value_source && asset.value_source !== 'manual' && (
                            <span className="text-ink-faint"> · via {valueSourceLabel(asset.value_source)}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-semibold text-green-900">{formatCurrency(asset.estimated_value)}</p>
                        {asset.debt_balance ? (
                          <p className="text-xs text-danger">Debt: {formatCurrency(asset.debt_balance)}</p>
                        ) : asset.assigned_to ? (
                          <p className="text-xs text-ink-faint">{getOwnerDisplayName(asset.assigned_to, caseData.client_name, caseData.opposing_party_name)}</p>
                        ) : null}
                      </div>
                      <Badge variant={getPropertyBadgeVariant(asset.property_status)}>
                        {getPropertyLabel(asset.property_status)}
                      </Badge>
                      {isExpanded ? <ChevronUp size={16} className="text-ink-faint" /> : <ChevronDown size={16} className="text-ink-faint" />}
                    </div>
                  </div>

                  {isExpanded && editForm && (
                    <div className="border-t border-cream-dark p-4 bg-cream">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select
                          label="Asset Type"
                          options={ASSET_TYPES}
                          value={editForm.asset_type}
                          onChange={(e) => setEditForm({ ...editForm, asset_type: e.target.value })}
                        />
                        <Input
                          label="Name"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          required
                        />
                        <Input
                          label="Estimated Value ($)"
                          type="number"
                          step="0.01"
                          value={editForm.estimated_value ?? ''}
                          onChange={(e) => setEditForm({ ...editForm, estimated_value: e.target.value ? parseFloat(e.target.value) : undefined })}
                        />
                        <Input
                          label="Acquisition Date"
                          type="date"
                          value={editForm.acquisition_date ?? ''}
                          onChange={(e) => setEditForm({ ...editForm, acquisition_date: e.target.value || undefined })}
                        />
                        <Select
                          label="Property Status"
                          options={PROPERTY_STATUSES}
                          value={editForm.property_status || 'CP_candidate'}
                          onChange={(e) => setEditForm({ ...editForm, property_status: e.target.value })}
                        />
                        <Select
                          label="Assigned To"
                          options={getAssignedOptions(caseData.client_name, caseData.opposing_party_name)}
                          value={editForm.assigned_to || ''}
                          onChange={(e) => setEditForm({ ...editForm, assigned_to: e.target.value || undefined })}
                        />
                        <Input
                          label="Debt Balance ($)"
                          type="number"
                          step="0.01"
                          value={editForm.debt_balance ?? ''}
                          onChange={(e) => setEditForm({ ...editForm, debt_balance: e.target.value ? parseFloat(e.target.value) : undefined })}
                        />
                        <Input
                          label="Cost Basis ($)"
                          type="number"
                          step="0.01"
                          value={editForm.cost_basis ?? ''}
                          onChange={(e) => setEditForm({ ...editForm, cost_basis: e.target.value ? parseFloat(e.target.value) : undefined })}
                        />
                        <Select
                          label="Sell Horizon"
                          options={SELL_HORIZONS}
                          value={editForm.sell_horizon || 'SELL_NOW'}
                          onChange={(e) => setEditForm({ ...editForm, sell_horizon: e.target.value })}
                        />
                        <Input
                          label="Address"
                          value={editForm.address ?? ''}
                          onChange={(e) => setEditForm({ ...editForm, address: e.target.value || undefined })}
                        />
                      </div>
                      {editForm.asset_type === 'Real Estate' && (
                        <label className="mt-3 flex items-center gap-2 text-sm text-ink-soft cursor-pointer">
                          {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-green-700"
                            checked={editForm.is_primary_residence ?? (asset.id === firstRealEstateId)}
                            onChange={(e) => setEditForm({ ...editForm, is_primary_residence: e.target.checked })}
                          />
                          Primary residence
                          <span className="text-xs text-ink-faint">(eligible for the §121 capital-gains exclusion; uncheck for a rental / investment property)</span>
                        </label>
                      )}
                      <div className="mt-4">
                        <TextArea
                          label="Notes"
                          value={editForm.notes ?? ''}
                          onChange={(e) => setEditForm({ ...editForm, notes: e.target.value || undefined })}
                        />
                      </div>
                      <div className="flex justify-between mt-4">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(asset); }}
                        >
                          <Trash2 size={14} className="mr-1" />
                          Delete
                        </Button>
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => { setExpandedId(null); setEditForm(null); }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            loading={updateMutation.isPending}
                            onClick={() => handleSaveEdit(asset.id)}
                            disabled={!editForm.name.trim()}
                          >
                            Save Changes
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ── DEBTS SECTION ───────────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="t-section text-green-900">Debts & Liabilities</h2>
            <p className="text-sm text-ink-faint mt-1">
              {liabilities.length} debt{liabilities.length !== 1 ? 's' : ''} · Total owed: {formatCurrency(totalDebt)}
              {assets.some(a => a.debt_balance) && (
                <span className="text-ink-faint"> (includes asset-linked balances)</span>
              )}
            </p>
          </div>
          <Button size="lg" icon={Plus} onClick={() => setShowDebtForm(!showDebtForm)}>
            Add Debt
          </Button>
        </div>

        {showDebtForm && (
          <Card>
            <form onSubmit={handleDebtSubmit} className="space-y-4">
              <h3 className="t-card text-green-900">New Debt</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Debt Type"
                  options={DEBT_TYPES}
                  value={debtForm.liability_type}
                  onChange={(e) => setDebtForm({ ...debtForm, liability_type: e.target.value })}
                />
                <Input
                  label="Creditor / Lender"
                  placeholder="e.g., Chase Bank, Navient, IRS"
                  value={debtForm.creditor_name}
                  onChange={(e) => setDebtForm({ ...debtForm, creditor_name: e.target.value })}
                  required
                />
                <Input
                  label="Balance Owed ($)"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={debtForm.balance ?? ''}
                  onChange={(e) => setDebtForm({ ...debtForm, balance: e.target.value ? parseFloat(e.target.value) : undefined })}
                />
                <Input
                  label="Account Last 4 Digits"
                  placeholder="e.g., 4521"
                  maxLength={4}
                  value={debtForm.account_last_four}
                  onChange={(e) => setDebtForm({ ...debtForm, account_last_four: e.target.value })}
                />
                <Select
                  label="Property Status"
                  options={PROPERTY_STATUSES}
                  value={debtForm.property_status}
                  onChange={(e) => setDebtForm({ ...debtForm, property_status: e.target.value })}
                />
                <Select
                  label="Assigned To"
                  options={getAssignedOptions(caseData.client_name, caseData.opposing_party_name)}
                  value={debtForm.assigned_to}
                  onChange={(e) => setDebtForm({ ...debtForm, assigned_to: e.target.value })}
                />
              </div>
              <TextArea
                label="Notes"
                placeholder="e.g., joint account, incurred during marriage, co-signer..."
                value={debtForm.notes}
                onChange={(e) => setDebtForm({ ...debtForm, notes: e.target.value })}
              />
              <div className="flex gap-2 justify-end">
                <Button variant="secondary" type="button" onClick={() => { setShowDebtForm(false); setDebtForm({ ...emptyDebtForm }); }}>
                  Cancel
                </Button>
                <Button type="submit" loading={createDebtMutation.isPending} disabled={!debtForm.creditor_name.trim()}>
                  Save Debt
                </Button>
              </div>
            </form>
          </Card>
        )}

        {liabilities.length === 0 && !showDebtForm ? (
          <Card padding="none">
            <EmptyState
              icon={CreditCard}
              title="No debts recorded"
              description="Add mortgages, auto loans, credit cards, student loans, and any other obligations."
            />
          </Card>
        ) : (
          <div className="space-y-2">
            {liabilities.map((liability) => {
              const isEditing = editDebtId === liability.id;
              return (
                <Card key={liability.id} padding={false}>
                  <div className="p-4">
                    {!isEditing ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-danger-soft rounded-lg">
                            <CreditCard size={18} className="text-danger" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-green-900">{liability.creditor_name}</h4>
                              {liability.account_last_four && (
                                <span className="t-ident text-ink-faint">···{liability.account_last_four}</span>
                              )}
                            </div>
                            <p className="text-xs text-ink-faint mt-1">
                              {liability.liability_type || 'Debt'}
                              {liability.assigned_to && (
                                <span> · {getOwnerDisplayName(liability.assigned_to, caseData.client_name, caseData.opposing_party_name)}</span>
                              )}
                            </p>
                            {liability.notes && (
                              <p className="text-xs text-ink-faint mt-1 italic">{liability.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-semibold text-danger">{formatCurrency(liability.balance)}</p>
                          </div>
                          <Badge variant={getPropertyBadgeVariant(liability.property_status || 'CP_candidate')}>
                            {getPropertyLabel(liability.property_status || 'CP_candidate')}
                          </Badge>
                          <IconButton
                            icon={Edit2}
                            size="sm"
                            variant="ghost"
                            aria-label="Edit"
                            onClick={() => handleEditDebt(liability)}
                            className="text-ink-faint hover:text-green-700"
                          />
                          <IconButton
                            icon={Trash2}
                            size="sm"
                            variant="danger-ghost"
                            aria-label="Delete"
                            onClick={() => setDeleteDebtTarget(liability)}
                            className="text-ink-faint hover:text-danger"
                          />
                        </div>
                      </div>
                    ) : editDebtForm && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Select
                            label="Debt Type"
                            options={DEBT_TYPES}
                            value={editDebtForm.liability_type}
                            onChange={(e) => setEditDebtForm({ ...editDebtForm, liability_type: e.target.value })}
                          />
                          <Input
                            label="Creditor / Lender"
                            value={editDebtForm.creditor_name}
                            onChange={(e) => setEditDebtForm({ ...editDebtForm, creditor_name: e.target.value })}
                            required
                          />
                          <Input
                            label="Balance Owed ($)"
                            type="number"
                            step="0.01"
                            value={editDebtForm.balance ?? ''}
                            onChange={(e) => setEditDebtForm({ ...editDebtForm, balance: e.target.value ? parseFloat(e.target.value) : undefined })}
                          />
                          <Input
                            label="Account Last 4 Digits"
                            maxLength={4}
                            value={editDebtForm.account_last_four}
                            onChange={(e) => setEditDebtForm({ ...editDebtForm, account_last_four: e.target.value })}
                          />
                          <Select
                            label="Property Status"
                            options={PROPERTY_STATUSES}
                            value={editDebtForm.property_status}
                            onChange={(e) => setEditDebtForm({ ...editDebtForm, property_status: e.target.value })}
                          />
                          <Select
                            label="Assigned To"
                            options={getAssignedOptions(caseData.client_name, caseData.opposing_party_name)}
                            value={editDebtForm.assigned_to}
                            onChange={(e) => setEditDebtForm({ ...editDebtForm, assigned_to: e.target.value })}
                          />
                        </div>
                        <TextArea
                          label="Notes"
                          value={editDebtForm.notes}
                          onChange={(e) => setEditDebtForm({ ...editDebtForm, notes: e.target.value })}
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => { setEditDebtId(null); setEditDebtForm(null); }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            loading={updateDebtMutation.isPending}
                            onClick={() => handleSaveDebt(liability.id)}
                            disabled={!editDebtForm.creditor_name.trim()}
                          >
                            Save Changes
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ── CONFIRM MODALS ──────────────────────────────────────────────── */}
      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete Asset"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
      />
      <ConfirmModal
        open={!!deleteDebtTarget}
        onClose={() => setDeleteDebtTarget(null)}
        onConfirm={() => deleteDebtTarget && deleteDebtMutation.mutate(deleteDebtTarget.id)}
        title="Delete Debt"
        message={`Remove "${deleteDebtTarget?.creditor_name}" from the debt list? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteDebtMutation.isPending}
      />
    </div>
  );
}
